<template>
  <BaseModal :value="show" @input="$emit('close')">
    <template v-slot:header>
      <div class="text-xl font-bold">Compare versions</div>
    </template>
    <div class="compare">
      <div v-if="loading" class="text-center py-8">
        <BaseMiniSpinner />
      </div>

      <div
        v-else-if="loadError"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
      >
        {{ loadError }}
        <button class="underline ml-2" @click="loadSnapshots">Retry</button>
      </div>

      <p v-else-if="options.length < 2" class="empty">
        At least two saved versions are needed to compare. Versions created
        before this feature only have a PDF and can't be compared.
      </p>

      <template v-else>
        <div class="selectors">
          <label>
            <span>From</span>
            <select v-model="fromId">
              <option
                v-for="option in options"
                :key="option.id"
                :value="option.id"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <i class="mdi mdi-arrow-right"></i>
          <label>
            <span>To</span>
            <select v-model="toId">
              <option
                v-for="option in options"
                :key="option.id"
                :value="option.id"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>

        <p v-if="!diff" class="empty">Could not read one of the versions.</p>
        <p v-else-if="!diff.hasChanges" class="empty">
          No differences between these versions.
        </p>

        <template v-else>
          <section v-for="group in fieldGroups" :key="group.title">
            <h4>{{ group.title }}</h4>
            <table class="diff-table">
              <tr v-for="change in group.changes" :key="change.label">
                <td class="field">{{ change.label }}</td>
                <td class="from">{{ change.from }}</td>
                <td class="to">{{ change.to }}</td>
              </tr>
            </table>
          </section>

          <section v-if="diff.items.length">
            <h4>Products</h4>
            <table class="diff-table">
              <template v-for="(item, index) in diff.items">
                <tr :key="`item-${index}`" class="item-row">
                  <td colspan="3">
                    <span class="badge" :class="item.status">{{
                      item.status
                    }}</span>
                    <b>{{ item.nkrCode }}</b>
                    <span class="description">{{ item.description }}</span>
                  </td>
                </tr>
                <tr
                  v-for="change in item.changes"
                  :key="`item-${index}-${change.label}`"
                >
                  <td class="field">{{ change.label }}</td>
                  <td class="from">{{ change.from }}</td>
                  <td class="to">{{ change.to }}</td>
                </tr>
              </template>
            </table>
          </section>
        </template>
      </template>
    </div>
  </BaseModal>
</template>

<script>
import { getOrderVersionSnapshots } from "@/api/store/OrdersService.js";
import { diffOrderSnapshots, parseSnapshot } from "@/utils/order-version-diff";

export default {
  name: "OrderVersionsCompareModal",
  props: {
    show: { type: Boolean, required: true },
    orderNumber: { type: [Number, String], required: true },
    versions: { type: Array, required: true }
  },
  data() {
    return {
      loading: false,
      loadError: null,
      snapshots: [],
      fromId: null,
      toId: null
    };
  },
  computed: {
    // Sólo las versiones con snapshot se pueden comparar.
    options() {
      return this.snapshots.map((snapshot, index) => {
        const version = this.versions.find(
          item => Number(item.snapshotId) === snapshot.id
        );
        const parts = [
          `#${index + 1}`,
          this.$options.filters.dateWithHours(snapshot.createdAt)
        ];
        if (version) {
          parts.push(version.actionType);
          if (version.adminName) parts.push(version.adminName);
        }
        return { id: snapshot.id, label: parts.join(" · ") };
      });
    },
    diff() {
      const from = this.snapshotById(this.fromId);
      const to = this.snapshotById(this.toId);
      if (!from || !to) return null;
      return diffOrderSnapshots(from, to);
    },
    fieldGroups() {
      if (!this.diff) return [];
      return [
        { title: "General", changes: this.diff.general },
        { title: "Addresses", changes: this.diff.addresses },
        { title: "Totals", changes: this.diff.totals }
      ].filter(group => group.changes.length);
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) this.loadSnapshots();
      }
    }
  },
  methods: {
    snapshotById(id) {
      const snapshot = this.snapshots.find(item => item.id === id);
      return snapshot ? parseSnapshot(snapshot.snapshotJson) : null;
    },
    async loadSnapshots() {
      this.loading = true;
      this.loadError = null;
      try {
        this.snapshots = await getOrderVersionSnapshots(this.orderNumber);
        // Por defecto: la penúltima contra la última.
        const count = this.snapshots.length;
        if (count >= 2) {
          this.fromId = this.snapshots[count - 2].id;
          this.toId = this.snapshots[count - 1].id;
        }
      } catch (err) {
        this.loadError = err.response?.data?.error ?? err.message;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.compare {
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
  padding: 1.5rem;
}

.empty {
  padding: 1.5rem 0;
  text-align: center;
  color: #6b7280;
}

.selectors {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 1.25rem;

  label {
    display: flex;
    flex: 1 1 260px;
    flex-direction: column;
    gap: 4px;
    font-size: 16px;
    font-weight: 600;
    color: #374151;
  }

  select {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 400;
  }

  i {
    padding-bottom: 6px;
    font-size: 18px;
    color: #9ca3af;
  }
}

section {
  margin-bottom: 1.25rem;
  overflow-x: auto;
}

h4 {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  color: #374151;
}

.diff-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 16px;

  td {
    padding: 6px 8px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: top;
  }

  .field {
    width: 25%;
    color: #6b7280;
  }

  .from {
    width: 37.5%;
    background: #fef2f2;
    color: #991b1b;
    text-decoration: line-through;
  }

  .to {
    width: 37.5%;
    background: #f0fdf4;
    color: #166534;
  }

  .item-row td {
    padding-top: 10px;
    background: #f9fafb;
  }
}

.description {
  margin-left: 6px;
  color: #6b7280;
}

.badge {
  display: inline-block;
  margin-right: 6px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;

  &.added {
    background: #dcfce7;
    color: #166534;
  }

  &.removed {
    background: #fee2e2;
    color: #991b1b;
  }

  &.changed {
    background: #fef3c7;
    color: #92400e;
  }
}
</style>
